import {DatePart, DateUtil, StyleUtil} from "@mcleod/core";

export class TimelineCalculator {
    private _startDate: Date;
    private _endDate: Date;
    private minuteMultiplier: number;
    private _oneBlockHours: number;
    private _singleBlockWidth: number;
    private _singleBlockWidthString: string;
    private _scaleMultiplier: number;

    constructor(startDate: Date, endDate: Date, oneBlockHours: number) {
        this.startDate = startDate;
        this.endDate = endDate;
        this.oneBlockHours = oneBlockHours;
    }

    public get startDate(): Date {
        return this._startDate;
    }

    private set startDate(value: Date) {
        this._startDate = value;
    }

    public get endDate(): Date {
        return this._endDate;
    }

    private set endDate(value: Date) {
        this._endDate = value;
    }

    get oneBlockHours() {
        return this._oneBlockHours;
    }

    private set oneBlockHours(value: number) {
        this._oneBlockHours = Math.max(Math.round(value), 1);
        this._scaleMultiplier = value < 1 ? 1 / value : 1;
        this.minuteMultiplier = ((24/this.oneBlockHours)/24)/60;
        const defaultCalculation = (this.oneBlockHours) / 4 // The following calculation assumes every 4 hours of time is 3rem wide (or, if you prefer, 1 day is 18rem wide)
        const calculatedWidth = (this.scaleMultiplier * 3 * this.oneBlockHours) / this.oneBlockHours; // This is used if block width is 1 or 2 to increase the width of columns
        this.singleBlockWidth = Math.max(calculatedWidth, defaultCalculation);
    }

    private get singleBlockWidth(): number {
        return this._singleBlockWidth;
    }

    private set singleBlockWidth(value: number) {
        this._singleBlockWidth = value;
        this.singleBlockWidthString = StyleUtil.numberAsRems(this.singleBlockWidth);
    }

    public get singleBlockWidthString(): string {
        return this._singleBlockWidthString;
    }

    private set singleBlockWidthString(value: string) {
        this._singleBlockWidthString = value;
    }

    get daysDisplayed(): number {
        return DateUtil.dateDiff(DatePart.DAY, this.endDate, this.startDate) + 1;
    }

    get blocksPerDay(): number {
        return 24 / this.oneBlockHours;
    }

    get blocksPerRow(): number {
        return this.blocksPerDay * this.daysDisplayed;
    }

    public get scaleMultiplier(): number {
        return this._scaleMultiplier;
    }

    private set scaleMultiplier(value: number) {
        this._scaleMultiplier = value;
    }

    getEndPosition(): number {
        return this.getLeft(DateUtil.setToBeginningOfDay(this.endDate)) + (this.singleBlockWidth * this.blocksPerDay);
    }

    pastEndDate(date: Date): boolean {
        return this.getLeft(date) > this.getEndPosition();
    }

    getLeft(date: Date): number {
        const minutes = DateUtil.dateDiff(DatePart.MINUTE, date, this.startDate);
        const remDiff = minutes * this.minuteMultiplier;
        const calculatedDiff = remDiff * this.singleBlockWidth;
        return calculatedDiff < 0 ? 0 : calculatedDiff;
    }

    getWidth(arrivalPosition: number, destDate: Date): string {
        const endPosition = this.getEndPosition();
        const position = this.pastEndDate(destDate) ? endPosition : this.getLeft(destDate);
        const resultAsNumber = position - arrivalPosition;
        return StyleUtil.numberAsRems(resultAsNumber);
    }

    todayInRange(): boolean {
        return DateUtil.dateDiff(DatePart.DAY, new Date(), this.startDate) >= 0 &&
            DateUtil.dateDiff(DatePart.DAY, new Date(), this.endDate) <= 0;
    }
}
